/**
 * Verify / Reject on a Done card (13-10), Oversight's lens, built to design/mock-13.
 *
 *   - Verify: one tap, through the outbox with the 6 s undo. A `sampled` row also offers
 *     "Sampled OK" (`Verdict = sampled-ok`).
 *   - Reject (E3, D-13-00-3): an inline reason, required, then `reject`. The hook reads the row's
 *     audit trail first: who completed it (`completedByUpn`, ISS-188) and how many times it was
 *     rejected before (`countRejections`), so the second rejection escalates to the manager and its
 *     toast says so.
 *   - When `canDoVerb` refuses (no self-verification, completer unknown, still checking), the card
 *     says why in its own note and shows no buttons (ISS-188: said, never hidden).
 *
 * Unreleased Done rows are verifiable and rejectable too (D-13-R-2 / D-13-R-7).
 */

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { QueueCard } from '../../lib/todayQueue';
import type { VerbInput } from '../../lib/actionVerbs';
import type { AuthorityContext } from '../../lib/actionAuthority';
import type { RunCopy, RunResult } from '../../hooks/useActionQueue';
import type { ActionRow } from '../../types/actionRegister';
import styles from './today.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

export interface VerifyActionsProps {
  card: QueueCard;
  busy: boolean;
  verify: (input: VerbInput, copy: RunCopy, ctx: AuthorityContext) => void;
  reject: (row: ActionRow, reason: string) => Promise<RunResult>;
  /** A refused or failed reject, in words, for the card's note. */
  onRefused: (reason: string) => void;
}

export default function VerifyActions({ card, busy, verify, reject, onRefused }: VerifyActionsProps) {
  const { row } = card;
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  const [checking, setChecking] = useState(false);
  const field = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  if (card.refusal) return null; // the card's note says why

  const ctx: AuthorityContext = { completedByUpn: card.completedByUpn };
  const doVerify = (sampled: boolean) =>
    verify(sampled ? { verdict: 'sampled-ok' } : {}, { title: `${sampled ? 'Sampled OK' : 'Verified'}: ${row.reference}`, description: row.title }, ctx);

  const doReject = async () => {
    const reason = why.trim();
    if (!reason || checking) return;
    setChecking(true);
    const result = await reject(row, reason);
    setChecking(false);
    if (result.ok) {
      setOpen(false);
      setWhy('');
    } else if (result.reason !== 'busy') {
      onRefused(result.reason);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void doReject();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const id = `v${row.id}`;
  return (
    <>
      {!open && (
        <div className={styles.verbs}>
          <button type="button" data-primary className={cx(styles.btn, styles.go)} onClick={() => doVerify(false)} disabled={busy}>
            Verify
          </button>
          {row.verificationMethod === 'sampled' && (
            <button type="button" className={cx(styles.btn, styles.line)} onClick={() => doVerify(true)} disabled={busy}>
              Sampled OK
            </button>
          )}
          <button type="button" className={cx(styles.btn, styles.line)} onClick={() => setOpen(true)} disabled={busy} aria-expanded={false}>
            Reject
          </button>
        </div>
      )}
      {open && (
        <div className={styles.drawer}>
          <label htmlFor={`${id}-why`}>What&rsquo;s missing? This goes back to its owner.</label>
          <div className={styles.field}>
            <input
              ref={field}
              id={`${id}-why`}
              className={styles.input}
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={onKey}
              placeholder="Say what needs to change"
              autoComplete="off"
            />
            <button type="button" className={cx(styles.btn, styles.line, styles.sm)} onClick={() => void doReject()} disabled={!why.trim() || busy || checking}>
              {checking ? 'Checking…' : 'Reject and return'}
            </button>
            <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
          <p className={styles.hint}>If it comes back a second time, their manager is told as well.</p>
        </div>
      )}
    </>
  );
}
